import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/daim6r3p14a1h3mr76r0";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = typeof value === "string" ? value.trim() : String(value);
      if (!text) return;
      payload.append(key, text);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后重试。";
      const isSpam = /spam|form data is spam/i.test(String(serverMessage));
      const isSuccess = response.ok && parsed?.code === "OK" && !isSpam;

      if (isSuccess) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(String(serverMessage));
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请检查网络后重试。");
    }
  };

  return (
    <form
      id="contact-line-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6"
    >
      <h3 className="font-heading text-base font-bold text-foreground-950">提交线索 / 联系我们</h3>
      <p className="mt-2 text-xs leading-relaxed text-foreground-500">
        补充后续回应、指出内容错误，或想聊聊合作，都可以在这里留言，我们通常会在三个工作日内回复。
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700">
            称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            autoComplete="name"
            placeholder="怎么称呼你"
            className="mt-1.5 h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700">
            邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="用于接收回复"
            className="mt-1.5 h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-topic" className="block text-xs font-medium text-foreground-700">
          事项类型
        </label>
        <select
          id="contact-topic"
          name="topic"
          defaultValue="补充线索"
          className="mt-1.5 h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
        >
          <option value="补充线索">补充线索</option>
          <option value="内容纠错">内容纠错</option>
          <option value="词条收录申请">词条收录申请</option>
          <option value="商务合作">商务合作</option>
        </select>
      </div>

      <input
        type="text"
        name="website_alt"
        className="contact-alt-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-4">
        <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700">
          具体说明
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请简单说明涉及的人物或事件名称，以及你希望补充的信息（最多 500 字）"
          className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
        />
      </div>

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        <i className="ri-send-plane-line" />
        {status === "submitting" ? "正在提交…" : "提交"}
      </button>

      {status === "success" && (
        <p className="mt-4 flex items-center gap-2 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-800">
          <i className="ri-checkbox-circle-line text-base" />
          已收到你的留言，我们会在三个工作日内回复，感谢补充。
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 flex items-center gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line text-base" />
          {formError}
        </p>
      )}
    </form>
  );
}